import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Video, Trash2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/auth/context/AuthContext'
import { useSeguimientos, useCrearSeguimiento, useEliminarSeguimiento } from '../hooks/useVisualizaciones'
import { VisualizacionForm } from '../components/VisualizacionForm'
import type { VideoSeguimientoCreateInput } from '../types'

export function VisualizacionesPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  // Crear/eliminar reportes solo para admin (o superusuario).
  const esAdmin = !!user && (user.is_superuser || user.groups.includes('admin'))
  const { data, isLoading, error } = useSeguimientos()
  const crearMut = useCrearSeguimiento()
  const eliminarMut = useEliminarSeguimiento()
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleCrear = async (formData: VideoSeguimientoCreateInput) => {
    await crearMut.mutateAsync(formData)
    setDialogOpen(false)
  }

  const handleEliminar = (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (confirm('¿Eliminar este seguimiento?')) {
      eliminarMut.mutate(id)
    }
  }

  const seguimientos = data?.results ?? []

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Visualizaciones de Videos</h1>
          <p className="text-muted-foreground">Reportes de visualizaciones por video</p>
        </div>
        {esAdmin && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-blue-800 hover:bg-blue-900 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Seguimiento
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>Error al cargar los seguimientos</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : seguimientos.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {seguimientos.map((seg) => (
            <Card
              key={seg.id}
              className="border border-gray-300 dark:border-gray-700 cursor-pointer transition-colors hover:border-blue-500"
              onClick={() => navigate(`/visualizaciones/${seg.id}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-md bg-blue-500/10 p-2">
                      <Video className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-semibold">{seg.nombre_video || `Video ${seg.id_video}`}</p>
                      <p className="text-sm text-muted-foreground">
                        {seg.base_wp} · ID {seg.id_video}
                      </p>
                    </div>
                  </div>
                  {esAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={(e) => handleEliminar(e, seg.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{seg.laboratorio_nombre}</span>
                  <Badge variant={seg.activo ? 'default' : 'secondary'}>
                    {seg.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="py-16 text-center">
          <Video className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="mb-4 text-muted-foreground">No hay seguimientos registrados</p>
          {esAdmin && (
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-blue-800 hover:bg-blue-900 text-white"
            >
              <Plus className="mr-2 h-4 w-4" />
              Crear el primero
            </Button>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Seguimiento de Video</DialogTitle>
          </DialogHeader>
          <VisualizacionForm onSubmit={handleCrear} isSubmitting={crearMut.isPending} />
        </DialogContent>
      </Dialog>
    </div>
  )
}
